/**
 * The charge rule sub-line — ONE implementation for every client- and
 * contractor-facing document.
 *
 *   "$0.77 / lb × 2,000 lb"
 *   "14% or $300, whichever is higher"
 *   "$25.00 × 3"
 *
 * This lived in ContractorDocView while the invoice portal formatted its own
 * rates a different way, off a pre-stringified value. Two formats for the same
 * idea on documents a client reads side by side. Now both call this.
 *
 * Returns null when a line has no rule worth stating — a flat amount explains
 * itself, and repeating it as "$150.00 × 1" is noise.
 */

import { formatMoney } from '../../lib/utils';

export interface RuleLine {
  rateKind?: string | null;
  rate?: number | null;
  quantity?: number | null;
  unitLabel?: string | null;
  percentage?: number | null;
  minimumAmount?: number | null;
}

export function chargeRule(l: RuleLine): string | null {
  if (!l) return null;

  if (l.rateKind === 'pct') {
    if (l.percentage == null) return null;
    // A floor is part of the rule: without it the stated percentage does not
    // reconcile with the amount printed beside it, which reads as an error.
    return Number(l.minimumAmount) > 0
      ? `${Number(l.percentage)}% or ${formatMoney(l.minimumAmount)}, whichever is higher`
      : `${Number(l.percentage)}% of the transportation charge`;
  }

  if (l.rate == null || l.quantity == null) return null;
  const qty = Number(l.quantity);
  if (!qty) return null;
  const rate = formatMoney(l.rate);

  if (l.unitLabel) return `${rate} / ${l.unitLabel} × ${qty.toLocaleString()} ${l.unitLabel}`;
  if (qty > 1) return `${rate} × ${qty.toLocaleString()}`;
  return null;
}
